/* OnSumn — WHAT THIS BROWSER KNOWS (G3a, read-only)
   The disclosure surface for on-device personalization. There are no
   accounts, so localStorage IS the user record — this panel is the one
   true answer to what it holds, in plain language, never a JSON dump.

   READ-ONLY this phase (G3b adds per-item delete, bulk clear, export).
   It reads through the EXISTING helpers — peekSavedTaste (the
   non-migrating entry to TasteProfile's single parser — Atlas hole 2: a
   disclosure read must never write, not even a schema stamp),
   readTasteNudgeState, readInterested (Interest.jsx), readHomeBase
   (lib/home-base.js), readNightDemandState (lib/night-demand.js) — and
   writes NOTHING: no migrate, no normalise, no re-stamp (byte-identical
   storage is a test gate, including a LEGACY v-less blob), no network
   (fetch/XHR/sendBeacon unreachable), and no analytics, ever: the one
   surface whose subject is "we are not watching you" cannot be
   instrumented.

   CROSS-PAGE TRUTH (Atlas hole 1): not every page ships every reader,
   and a guarded reader that quietly renders "No X saved" on the wrong
   page is a disclosure panel telling false negatives — worse than no
   panel. Rule: when a row's reader is absent, the row falls back to a
   RAW PRESENCE check (key exists → a true "saved here" line pointing at
   the home page; key absent → the honest empty state). Zero false
   absences on any page, gated with readers stubbed out.

   Scope — the bucket table IS the design (G3 brief + Atlas ruling 1), and it
   now lives in ONE machine-readable place: data/storage-inventory.js.
   It used to be enumerated here in prose, which is how onsumn-interest-cache
   :{slug} went missing from this panel in the first place — it is written as
   `'onsumn-interest-cache:' + slug`, invisible to a literal-key grep, and only
   a human audit caught it. tests/storage-inventory.test.js now scans every
   setItem() in shipped source, resolves the key behind it (literal, named
   constant, or prefix concatenation), and fails when anything is written that
   the inventory does not declare — plus fails if a key declared PERSONAL never
   appears in this file. Do not restate the bucket list here; a second copy is
   the bug.

   Bucket meanings: personal = inferred from behaviour, shown here and
   erasable here · displayPrefs = chosen deliberately and already visible in
   the UI · machinery = operator credentials and plumbing (echoing a token
   into user DOM is a security defect, not transparency).

   The interest-cache is shown as an AGGREGATE row on purpose — a per-slug
   listing would be the JSON dump this panel exists to prevent.

   onsumn-pin is a Leaflet className (Map.jsx), not storage — its absence
   here is pinned by test. */

/* Pacific-calendar date for "WHEN" lines (feedback_pacific_time). */
function bkWhen(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return null;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles', month: 'long', day: 'numeric', year: 'numeric',
    }).format(new Date(ms));
  } catch { return null; }
}

/* 'muni-west-portal' → "West Portal (MUNI)" — presentation only. */
function bkStationName(id) {
  if (typeof id !== 'string' || !id) return null;
  const parts = id.split('-').filter(Boolean);
  if (parts.length < 2) return id;
  const system = parts[0].toUpperCase();
  const name = parts.slice(1).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  return `${name} (${system})`;
}

/* '2026W30' → "July 20, 2026" (that ISO week's Monday) — presentation only.
   Unparseable keys return null; callers degrade to the raw-key sentence
   rather than inventing a date (Rhea ruling). */
function bkWeekOf(weekKey) {
  const m = typeof weekKey === 'string' && weekKey.match(/^(\d{4})W(\d{1,2})$/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const week = parseInt(m[2], 10);
  if (week < 1 || week > 53) return null;
  // ISO week 1 contains Jan 4. Monday of week W = Jan 4 − (isoDay(Jan4) − 1) + (W−1)·7.
  const jan4 = Date.UTC(year, 0, 4);
  const isoDay = (new Date(jan4).getUTCDay() || 7);
  const monday = new Date(jan4 - (isoDay - 1) * 86400000 + (week - 1) * 7 * 86400000);
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  return `${MONTHS[monday.getUTCMonth()]} ${monday.getUTCDate()}, ${monday.getUTCFullYear()}`;
}

/* Raw presence check for rows whose plain-language reader this page does
   not ship — TRUE statements only (Atlas hole 1). Presence test, never a
   parse. Also drives per-row delete visibility (G3b): a delete control on
   an empty row is noise. */
function bkRawPresence(key) {
  try { return localStorage.getItem(key) != null; } catch { return false; }
}

/* ── G3b: the erase half ──
   Per-row delete + one-confirm bulk clear + client-side export, over
   EXACTLY the Atlas-ratified (a) set. Doctrine:
     · No undo. An erase the user can reverse is an erase we are still
       holding.
     · No instrumentation. How many people erase their data is the one
       measurement this surface can never take.
     · One confirm on the bulk path, DECLARATIVE form (Mav's registry
       constraint bans interrogative confirms repo-wide) — no retention
       offer, no second confirm: §2.4 confirmshaming, banned by name and
       lexicon-enforced.
     · (b)/(c) survive every erase — wiping a profile must not throw the
       user into the wrong color scheme; and per Ledger, the copy never
       says "everything" while (b)/(c) survive: it says "every record
       listed here."
     · Deletion goes through the EXISTING eraser where one exists
       (forgetHomeBase — spec §1.2 removes the key entirely); no writer is
       touched to make deletion tidier. */
const BK_CACHE_PREFIX = 'onsumn-interest-cache:';
const BK_LOCAL_KEYS = ['onsumn-taste', 'onsumn-taste-nudge', 'onsumn-interests', 'onsumn-night-demand', 'onsumn-section-order'];

function bkCacheFamilyKeys() {
  const keys = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.indexOf(BK_CACHE_PREFIX) === 0) keys.push(k);
    }
  } catch {}
  return keys;
}

/* NO RESURRECTION (lite-spec §3, the same bar F4 sets on /flows): while a
   setup is attached, a local erase of a SYNCED record writes through to the
   profile. A record the user asked us to forget that re-appears from the
   server on the next load is a record we kept after being asked not to.
   Fire-and-forget: this panel still never surfaces a network error, and an
   unreachable profile leaves the local erase exactly as effective as it is
   today. The three mapped keys are the whole envelope; nothing else here has
   a server twin. */
const BK_PROFILE_TWIN = {
  'onsumn-home-v1': 'homeStation',
  'onsumn-taste': 'taste',
  'onsumn-interests': 'marks',
};

function bkForgetRemotely(key) {
  const field = BK_PROFILE_TWIN[key];
  if (!field) return;
  const acct = (typeof window !== 'undefined') ? window.OnSumnAccount : null;
  if (!acct || typeof acct.removeField !== 'function' || !acct.isAttached()) return;
  try { const p = acct.removeField(field); if (p && p.catch) p.catch(() => {}); } catch {}
}

function bkDeleteKey(key) {
  bkForgetRemotely(key);
  try {
    if (key === 'onsumn-home-v1') {
      if (typeof forgetHomeBase === 'function') { forgetHomeBase(); return; }
      localStorage.removeItem(key); return;
    }
    if (key === 'onsumn-taste-nudge-visit') { sessionStorage.removeItem(key); return; }
    if (key === 'onsumn-interest-cache') {
      bkCacheFamilyKeys().forEach((k) => { try { localStorage.removeItem(k); } catch {} });
      return;
    }
    localStorage.removeItem(key);
  } catch {}
}

function bkClearAll() {
  BK_LOCAL_KEYS.forEach((k) => {
    bkForgetRemotely(k); // the bulk path owes the same no-resurrection bar
    try { localStorage.removeItem(k); } catch {}
  });
  bkDeleteKey('onsumn-home-v1');
  bkDeleteKey('onsumn-taste-nudge-visit');
  bkDeleteKey('onsumn-interest-cache');
}

/* Export — the (a) records exactly as stored (the user's own file may be
   complete per-slug; the no-JSON-dump rule governs the RENDERED panel,
   not what the user takes with them). Client-side Blob download; no
   network, ever. */
function bkBuildExport() {
  const records = {};
  BK_LOCAL_KEYS.concat(['onsumn-home-v1']).forEach((k) => {
    try { const v = localStorage.getItem(k); if (v != null) records[k] = v; } catch {}
  });
  try {
    const v = sessionStorage.getItem('onsumn-taste-nudge-visit');
    if (v != null) records['onsumn-taste-nudge-visit'] = v;
  } catch {}
  bkCacheFamilyKeys().forEach((k) => {
    try { const v = localStorage.getItem(k); if (v != null) records[k] = v; } catch {}
  });
  return JSON.stringify({
    site: 'onsumn.com',
    exportedAt: new Date().toISOString(),
    note: 'Raw records exactly as stored in this browser.',
    records,
  }, null, 2);
}

function bkDownloadExport() {
  const blob = new Blob([bkBuildExport()], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'what-this-browser-knows.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 0);
}
const BK_FALLBACK = 'Saved in this browser. Open this panel from the home page to see it in plain terms.';

/* Aggregate count of onsumn-interest-cache:{slug} entries — prefix scan,
   counts keys only, parses nothing. */
function bkCountCacheEntries() {
  let n = 0;
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.indexOf('onsumn-interest-cache:') === 0) n += 1;
    }
  } catch {}
  return n;
}

/* Row builders — { key, label, what, when, why }. `when` is a complete
   sentence so the verb travels with the row (Saved / Noted / Last shown —
   Rhea ruling). Unreadable data degrades to the honest state, never a
   throw; missing readers degrade to bkRawPresence, never a false absence. */
function bkRows() {
  const rows = [];

  /* Home station first — a LOCATION is the most sensitive thing here. */
  {
    let what = 'No home station saved.';
    let when = null;
    if (typeof readHomeBase === 'function') {
      const rec = readHomeBase();
      if (rec && rec.stationId) {
        what = `Your home station: ${bkStationName(rec.stationId)}${rec.source === 'gps' ? ' — set from your location' : ''}.`;
        const d = bkWhen(rec.setAt);
        when = d ? `Saved ${d}.` : null;
      } else if (rec && rec.promptDismissedAt) {
        what = 'You said "not now" to the home-station ask.';
        const d = bkWhen(rec.promptDismissedAt);
        when = d ? `Noted ${d}.` : null;
      }
    } else if (bkRawPresence('onsumn-home-v1')) {
      what = BK_FALLBACK;
    }
    rows.push({
      key: 'onsumn-home-v1', label: 'HOME STATION', what, when,
      why: 'So Bay Flows can point the last train back to your door.',
    });
  }

  {
    const ttlDays = (typeof window !== 'undefined' && typeof window.TASTE_TTL_MS === 'number')
      ? Math.round(window.TASTE_TTL_MS / 86400000) : null;
    let what = 'No taste profile saved.';
    let when = null;
    if (typeof peekSavedTaste === 'function') {
      const taste = peekSavedTaste();
      if (taste && taste.skipped) {
        what = 'You skipped the taste picker — no picks stored.';
        const d = bkWhen(taste.timestamp);
        when = d ? `Noted ${d}.` : null;
      } else if (taste) {
        const picks = [...(taste.vibes || []), ...(taste.sounds || [])];
        what = picks.length
          ? `You picked ${picks.join(', ')}.`
          : 'You saved a taste profile with no picks.';
        const d = bkWhen(taste.timestamp);
        when = d ? `Saved ${d}.` : null;
      }
    } else if (bkRawPresence('onsumn-taste')) {
      what = BK_FALLBACK;
    }
    rows.push({
      key: 'onsumn-taste', label: 'TASTE', what, when,
      why: `Pulls your kind of shows into the YOUR WAVE rail.${ttlDays ? ` Kept ${ttlDays} days from your last save, then it expires on its own.` : ''}`,
    });
  }

  /* SLIDING — the interface's word (Rhea ruling: the pill renders
     "N SLIDING"; capture is implicit — interest is recorded when you tap
     through to tickets, and this row says so). */
  {
    let what = "You haven't tapped through to any shows.";
    if (typeof readInterested === 'function') {
      const slugs = readInterested();
      if (slugs.length) {
        what = `You tapped through to ${slugs.length} ${slugs.length === 1 ? 'show' : 'shows'} — that counts as sliding.`;
      }
    } else if (bkRawPresence('onsumn-interests')) {
      what = BK_FALLBACK;
    }
    rows.push({
      key: 'onsumn-interests', label: 'SLIDING', what, when: null,
      why: 'Feeds the sliding counts on event cards.',
    });
  }

  /* Count cache — Atlas ruling 1: a concatenated-key family the literal
     grep missed. ONE aggregate row; never per-slug. */
  {
    const n = bkCountCacheEntries();
    rows.push({
      key: 'onsumn-interest-cache', label: 'COUNT CACHE',
      what: n > 0
        ? (n === 1
          ? 'The public sliding count for 1 card is cached here.'
          : `The public sliding counts for ${n} cards are cached here.`)
        : 'No counts cached.',
      when: null,
      why: 'So recently seen cards do not have to look up their counts again.',
    });
  }

  {
    let what = 'The taste ask has not been dismissed.';
    let when = null;
    if (typeof readTasteNudgeState === 'function') {
      const s = readTasteNudgeState();
      if (s.shownCount > 0) what = `You dismissed the taste ask ${s.shownCount} of 3 times.`;
      const d = bkWhen(s.lastShownAt);
      when = d ? `Last shown ${d}.` : null;
    } else if (bkRawPresence('onsumn-taste-nudge')) {
      what = BK_FALLBACK;
    }
    rows.push({
      key: 'onsumn-taste-nudge', label: 'TASTE ASK COUNTER', what, when,
      why: 'After three dismissals it stops appearing on its own.',
    });
  }

  {
    let seen = false;
    try { seen = !!sessionStorage.getItem('onsumn-taste-nudge-visit'); } catch {}
    rows.push({
      key: 'onsumn-taste-nudge-visit', label: 'THIS-VISIT MARKER',
      what: seen
        ? 'The taste ask already showed this visit.'
        : 'The taste ask has not shown this visit.',
      when: null,
      why: 'Caps the ask at once per visit; clears when the tab closes.',
    });
  }

  {
    let what = 'The probe has not run in this browser.';
    if (typeof readNightDemandState === 'function') {
      const st = readNightDemandState();
      if (st && st.lastBeaconWeek) {
        const weekOf = bkWeekOf(st.lastBeaconWeek);
        what = weekOf
          ? `An anonymous once-a-week probe about how nights get planned last ran the week of ${weekOf}.`
          : `An anonymous once-a-week probe about how nights get planned last ran in week ${st.lastBeaconWeek}.`;
      }
    } else if (bkRawPresence('onsumn-night-demand')) {
      what = BK_FALLBACK;
    }
    rows.push({
      key: 'onsumn-night-demand', label: 'NIGHT PROBE', what, when: null,
      why: 'Counts how your picks cluster into nights and adds that to anonymous weekly totals — no identity attached.',
    });
  }

  {
    let order = null;
    try {
      const parsed = JSON.parse(localStorage.getItem('onsumn-section-order'));
      if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) order = parsed;
    } catch {}
    rows.push({
      key: 'onsumn-section-order', label: 'ROW ORDER',
      what: order
        ? `You arranged the home rows: ${order.join(' → ')}.`
        : 'Home rows are in the default order.',
      when: null,
      why: 'So your order sticks next visit — you set it by dragging rows on the home page.',
    });
  }

  return rows;
}

/* The disclosure line — Atlas ruling 2 (amend: name BOTH egresses, keep
   claims scoped to the listed records) composed in Rhea's vocabulary
   (sliding, tap-through, "erases it all"). Byte-equality is a test gate;
   changing this line is deliberate by construction.

   ── R6 RE-PIN, 2026-08-26 (account-lite, same PR — the ship cannot leave
   this sentence lying) ──
   Three clauses of the previous wording became CONDITIONALLY FALSE the
   moment a browser can carry a claimed setup:
     · "tied to no name or account" — a claimed setup is keyed to an address
       the user typed. Unconditionally dropped; the truthful form makes the
       condition explicit instead of asserting the absence.
     · "ONLY two things ever leave" — three records also travel while
       attached. "Only" is gone; the two anonymous egresses are still named
       because they happen either way.
     · "Clearing your browser data erases it all" — true locally, false for
       the server copy. Now scoped, with the surface that CAN erase it named,
       so the sentence points at a remedy instead of over-claiming.
   The word "account" still renders nowhere (Marco's ruling); the noun is
   "setup". Zero digits.

   ── ATLAS SIGNED TEXT, 2026-08-27. Kai's draft above was REJECTED. It read
   "Nothing here is tied to a name unless you set up WHAT FOLLOWS YOU", and
   a NAME is the one identifier class this store never holds — the binding
   identifier is the ADDRESS the user typed, which the draft's own next
   clause then said out loud. Naming the wrong identifier class to deny it
   is worse than silence: it invites the reader to conclude the address is
   not an identifier. Atlas's text asserts no absence at all; it states the
   condition and names the identifier once, correctly.

   FIVE FACTS THAT MUST SURVIVE ANY EDIT (each pinned in
   tests/browser-knows.test.js):
     1. BOTH egresses named, and UNCONDITIONAL — they happen whether or not
        a setup exists. The word "Only" is dead and must not return.
     2. The second store named, and CONDITIONAL on claiming a setup.
     3. The identifier is the typed ADDRESS. Never "name", never "account".
     4. Clearing this browser does NOT reach the server copy.
     5. The remedy surface is named (WHAT FOLLOWS YOU). */
const BK_DISCLOSURE = 'All of this lives in this browser. Two things always leave, both anonymous: tapping through to a show bumps its public sliding count, and once a week the night probe adds to the totals. If you set up WHAT FOLLOWS YOU, your home station, wave, and marks are also kept under the address you typed — clearing this browser leaves that copy standing. WHAT FOLLOWS YOU is where you see it, change it, or wipe it.';

function BrowserKnowsPanel({ darkMode, onClose }) {
  /* G3b: rows re-read after each delete (tick). Reading still writes
     nothing — the only writes are the user's explicit deletes. */
  const [tick, setTick] = React.useState(0);
  const [confirming, setConfirming] = React.useState(false);
  const [announce, setAnnounce] = React.useState('');
  const rows = React.useMemo(() => bkRows(), [tick]); // eslint-disable-line
  const rootRef = React.useRef(null);
  const headingRef = React.useRef(null);
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;

  /* Row presence by RAW key existence — uniform and honest; drives
     whether the delete control renders (deleting a row's last data leaves
     the empty state, not a hole — and no control on an empty row). */
  const present = (key) => {
    if (key === 'onsumn-taste-nudge-visit') {
      try { return !!sessionStorage.getItem(key); } catch { return false; }
    }
    if (key === 'onsumn-interest-cache') return bkCacheFamilyKeys().length > 0;
    return bkRawPresence(key);
  };

  /* Focus management on the erase paths (Iris spec, G3b review): every
     activated control that unmounts hands focus somewhere deliberate —
     confirm strip's SENTENCE on open (never the destructive button:
     focus landing on it invites muscle-memory double-activation),
     invoker on decline, next remaining delete control after a row delete
     (previous if none after, export if none remain), heading after the
     bulk clear. pendingFocus is applied after the commit; a vanished
     target falls back to export. */
  const pendingFocus = React.useRef(null);
  React.useEffect(() => {
    const sel = pendingFocus.current;
    if (!sel || !rootRef.current) return;
    pendingFocus.current = null;
    const el = rootRef.current.querySelector(sel)
      || (sel !== '[data-bk-export]' ? rootRef.current.querySelector('[data-bk-export]') : null);
    if (el) el.focus();
  });

  const handleDelete = (r) => {
    if (rootRef.current) {
      const btns = Array.from(rootRef.current.querySelectorAll('[data-bk-delete]'));
      const i = btns.findIndex((b) => b.getAttribute('data-bk-delete') === r.key);
      const next = btns[i + 1] || btns[i - 1];
      pendingFocus.current = next
        ? `[data-bk-delete="${next.getAttribute('data-bk-delete')}"]`
        : '[data-bk-export]';
    }
    bkDeleteKey(r.key);
    /* aria-live announcement — a screen-reader user hears the row go.
       Copy Rhea-signed. */
    setAnnounce(`${r.label.charAt(0)}${r.label.slice(1).toLowerCase()} record deleted.`);
    setConfirming(false);
    setTick((t) => t + 1);
  };

  const handleConfirmOpen = () => {
    pendingFocus.current = '[data-bk-confirm-sentence]';
    setConfirming(true);
  };

  const handleKeepIt = () => {
    pendingFocus.current = '[data-bk-clear-all]';
    setConfirming(false);
  };

  const handleClearAll = () => {
    pendingFocus.current = '#bk-title';
    bkClearAll();
    setAnnounce('Every record listed here deleted.');
    setConfirming(false);
    setTick((t) => t + 1);
  };

  const handleExport = () => { bkDownloadExport(); };

  React.useEffect(() => {
    const opener = document.activeElement;
    if (headingRef.current) headingRef.current.focus();
    return () => {
      const valid = opener && opener !== document.body && opener !== document.documentElement
        && opener.isConnected && typeof opener.focus === 'function';
      if (valid) { opener.focus(); return; }
      const link = document.querySelector('[data-bk-open]');
      if (link) link.focus();
    };
  }, []);

  /* Esc closes (capture); Tab is trapped — INCLUDING from the heading:
     the heading is focused but not focusable, so without the outside-guard
     a first Tab (or Shift+Tab) would walk out of an aria-modal dialog
     (Iris fail 2). */
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); closeRef.current(); return; }
      if (e.key !== 'Tab' || !rootRef.current) return;
      const f = rootRef.current.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      const active = document.activeElement;
      const activeInList = Array.prototype.indexOf.call(f, active) !== -1;
      if (!activeInList) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  const ink = darkMode ? '#fff' : '#0A0A0A';
  const quiet = darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)';
  /* Faint row labels — Iris fail 1: ≥4.5:1 at 10.5px/700 (computed:
     0.48 white → 4.97:1 on #0A0A0A; 0.55 black → 4.70:1 on #FAF7F1). */
  const faint = darkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.55)';
  const hairline = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9995, /* modal band (9990+) */
        background: darkMode ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={rootRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bk-title"
        style={{
          position: 'relative',
          width: '100%', maxWidth: 640, maxHeight: '86vh',
          display: 'flex', flexDirection: 'column',
          /* #0A0A0A — TasteProfile-overlay parity (Zora: no one-off dark
             surfaces; the taste family reads as flat paper, not glass). */
          background: darkMode ? '#0A0A0A' : '#FAF7F1',
          border: hairline, borderRadius: 18,
          boxShadow: '0 24px 80px rgba(0,0,0,0.45)',
          overflow: 'hidden',
        }}
      >
        {/* Fixed header — the dismiss affordance must not scroll away on
            phones (Vale/Zora): heading + subline + ✕ stay put, rows
            scroll beneath. ✕ sits AFTER the heading in DOM so natural Tab
            order flows heading → ✕ → (rows). */}
        <div style={{ position: 'relative', padding: 'clamp(20px, 4vw, 32px)', paddingBottom: 12, flexShrink: 0 }}>
          <h2 id="bk-title" ref={headingRef} tabIndex={-1} style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(20px, 3.4vw, 26px)',
            fontWeight: 800, letterSpacing: '0.01em', textTransform: 'uppercase',
            color: ink, margin: '4px 0 6px', outline: 'none', paddingRight: 56,
          }}>What This Browser Knows</h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.5,
            color: quiet, margin: 0,
          }}>Everything this site keeps in this browser, in plain terms.</p>
          <button
            type="button"
            className="focus-double-ring"
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute', top: 12, right: 12,
              width: 48, height: 48, borderRadius: 100,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 700, lineHeight: 1,
              background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
              color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
              border: 'none', cursor: 'pointer',
            }}
          >✕</button>
        </div>

        {/* Scrolling body */}
        <div style={{ overflowY: 'auto', padding: '0 clamp(20px, 4vw, 32px) clamp(20px, 4vw, 32px)' }}>
          <div>
            {rows.map((r) => (
              /* Two-column row: text + a reserved 44px trailing slot so
                 G3b's per-row delete lands with zero re-layout of a
                 shipped trust surface (Zora). */
              <div key={r.key} data-bk-key={r.key} style={{
                padding: '14px 0', borderTop: hairline,
                display: 'flex', gap: 12, alignItems: 'center',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'var(--font-body)', fontSize: 10.5, fontWeight: 700,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: faint, marginBottom: 5,
                  }}>{r.label}</div>
                  <div style={{
                    fontFamily: 'var(--font-body)', fontSize: 14.5, lineHeight: 1.5,
                    color: ink,
                  }}>
                    {r.what}
                    {r.when ? <span style={{ color: quiet }}>{` ${r.when}`}</span> : null}
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-body)', fontSize: 12, lineHeight: 1.5,
                    color: quiet, marginTop: 3,
                  }}>{r.why}</div>
                </div>
                <div data-bk-slot style={{ width: 44, flexShrink: 0 }}>
                  {present(r.key) && (
                    /* Per-row delete in Zora's reserved slot — 44×44 (the
                       slot's own spec; house floor). Immediate, visible,
                       no confirm on a single row (G2a precedent: friction
                       encodes stakes, and one row is cheap to recreate). */
                    <button
                      type="button"
                      className="focus-double-ring"
                      data-bk-delete={r.key}
                      onClick={() => handleDelete(r)}
                      aria-label={`Delete ${r.label.toLowerCase()} record`}
                      style={{
                        width: 44, height: 44, borderRadius: 100,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700, lineHeight: 1,
                        background: 'transparent',
                        border: darkMode ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(0,0,0,0.22)',
                        color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                        cursor: 'pointer',
                      }}
                    >✕</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* G3b actions — export + one-confirm bulk clear. The confirm is
              ONE declarative step (Mav: no interrogatives; §2.4: no
              retention offer, no second confirm) and its copy never says
              "everything" while (b)/(c) survive (Ledger). All strings
              drafted by Marco — Rhea + Atlas hold final wording. */}
          {!confirming ? (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', margin: '18px 0 0', paddingTop: 14, borderTop: hairline }}>
              <button
                type="button"
                className="focus-double-ring"
                data-bk-export
                onClick={handleExport}
                style={{
                  minHeight: 48, padding: '0 18px', borderRadius: 100,
                  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  background: 'transparent',
                  border: darkMode ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(0,0,0,0.25)',
                  color: darkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)',
                  cursor: 'pointer',
                }}
              >Download a copy</button>
              <button
                type="button"
                className="focus-double-ring"
                data-bk-clear-all
                onClick={handleConfirmOpen}
                style={{
                  minHeight: 48, padding: '0 18px', borderRadius: 100,
                  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  background: 'transparent',
                  border: darkMode ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(0,0,0,0.25)',
                  color: darkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)',
                  cursor: 'pointer',
                }}
              >Clear all of this</button>
            </div>
          ) : (
            <div data-bk-confirm style={{ margin: '18px 0 0', paddingTop: 14, borderTop: hairline }}>
              <p data-bk-confirm-sentence tabIndex={-1} style={{
                fontFamily: 'var(--font-body)', fontSize: 13.5, lineHeight: 1.5,
                color: ink, margin: '0 0 12px', outline: 'none',
              }}>This deletes every record listed here from this browser.</p>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  className="focus-double-ring"
                  data-bk-confirm-yes
                  onClick={handleClearAll}
                  style={{
                    minHeight: 48, padding: '0 20px', borderRadius: 100,
                    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    background: 'var(--international-orange)', color: '#10110F',
                    border: 'none', cursor: 'pointer',
                  }}
                >Delete it all</button>
                <button
                  type="button"
                  className="focus-double-ring"
                  data-bk-confirm-no
                  onClick={handleKeepIt}
                  style={{
                    minHeight: 48, padding: '0 14px', borderRadius: 100,
                    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    background: 'transparent', color: quiet,
                    border: 'none', cursor: 'pointer',
                  }}
                >Keep it</button>
              </div>
            </div>
          )}

          <p data-bk-disclosure style={{
            fontFamily: 'var(--font-body)', fontSize: 12.5, lineHeight: 1.55,
            color: quiet, margin: '16px 0 0', paddingTop: 14, borderTop: hairline,
          }}>{BK_DISCLOSURE}</p>

          {/* Screen-reader announcement channel for deletes (Iris gate) —
              visually hidden, never displayed. */}
          <div data-bk-live aria-live="polite" style={{
            position: 'absolute', width: 1, height: 1, margin: -1, padding: 0,
            overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
          }}>{announce}</div>
        </div>
      </div>
    </div>
  );
}

BrowserKnowsPanel.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

window.BrowserKnowsPanel = BrowserKnowsPanel;
/* Twin-surface parity (account-lite): WHAT FOLLOWS YOU spells a station the
   same way this panel does. One presenter, two surfaces — a second copy is
   two spellings of one station. */
window.bkStationName = bkStationName;
